import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { SharedService } from '@app/core';
import { forkJoin, Subject } from 'rxjs';
import { SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Child component that encapsulates Udaan-specific logic:
 * - Proceed-to-buy flow (forkJoin cart sync + redirect)
 *
 * Udaan has minimal supplier-specific code. This component exists for
 * consistency and future extensibility.
 */
@Component({
    selector: 'app-udaan-supplier',
    template: '', // Udaan has no standalone template; all UI is in the parent
    styles: []
})
export class UdaanSupplierComponent implements OnDestroy {

    // ── Inputs from parent ──
    @Input() entityId = '';
    @Input() storeUuid = '';
    @Input() supplierSection: SupplierSection | null = null;
    
    // ── Cart State ──
    apiCartItems: SupplierProduct[] = [];
    apiCartCount = 0;
    cartQuantityMap = new Map<string, number>();

    // ── Outputs to parent ──
    @Output() checkoutComplete = new EventEmitter<void>();

    private destroy$ = new Subject<void>();

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Called by parent after entity changes.
     */
    resetState(): void {
        this.apiCartItems = [];
        this.apiCartCount = 0;
        this.cartQuantityMap.clear();
    }

    /**
     * Fetches the current Udaan cart and populates the quantity map.
     */
    fetchCart(): void {
        if (!this.entityId || !this.storeUuid) return;

        this.priceDiscoveryService.getUdaanCart(this.storeUuid, this.entityId)
            .subscribe({
                next: (response) => {
                    const items = response?.data?.items || [];
                    this.apiCartItems = items;
                    this.apiCartCount = items.length;
                    
                    this.cartQuantityMap.clear();
                    items.forEach((item: any) => {
                        if (item.productId) {
                            this.cartQuantityMap.set(String(item.productId), Number(item.qty) || 0);
                        }
                    });
                },
                error: (err) => {
                    console.error('❌ Failed to fetch Udaan cart', err);
                }
            });
    }

    /**
     * Called when an item is first added to the cart
     */
    addToCart(item: SupplierProduct, supplierName: string, previousQuantity: number): void {
        this.updateUdaanCartSync(item, item.selectedQuantity || 1);
    }

    /**
     * Called when an item's quantity is updated (incremented/decremented)
     */
    updateQuantity(item: SupplierProduct, change: number, previousQuantity: number): void {
        this.updateUdaanCartSync(item, item.selectedQuantity || 0);
    }

    /**
     * Internal helper for syncing Udaan cart in real-time
     */
    private updateUdaanCartSync(item: SupplierProduct, qty: number): void {
        item.isAddingToCart = true;
        this.priceDiscoveryService.updateUdaanCart(
            item.sku_id,
            qty,
            'Delivery',
            this.storeUuid,
            item.category_id,
            this.entityId,
            item.vertical || '',
            true // skipRedirect: don't open Udaan yet
        ).subscribe({
            next: () => {
                console.log(`✅ Udaan item "${item.item_name}" synced with qty: ${qty}`);
                item.isAddingToCart = false;
            },
            error: (err) => {
                console.error(`❌ Failed to sync Udaan item "${item.item_name}"`, err);
                item.isAddingToCart = false;
                this.sharedService.errorAlert(`Failed to sync "${item.item_name}" with Udaan`);
            }
        });
    }

    /**
     * Proceed-to-buy flow: Already synced, just redirect a and clear local state.
     */
    proceedToBuy(checkoutItems: SupplierProduct[]): void {
        if (!checkoutItems || checkoutItems.length === 0) return;

        // Since items are already being synced in real-time, just clear local state and redirect
        this.sharedService.successAlert('Redirecting to Udaan...');

        // Clear Udaan cart data from the supplier section
        if (this.supplierSection) {
            this.supplierSection.items.forEach(item => {
                item.selectedQuantity = 0;
            });
        }

        window.open('https://udaan.com/boltCart/?utm_source=petpooja', '_blank');
        this.checkoutComplete.emit();
    }
}
